<template>
  <div class="calendar-container">
    <CalendarHeader
      v-model:selectedStore="selectedStore"
      :stores="stores"
      @printRequested="handlePrintCalendar"
      @emailRequested="handleEmailCalendar"
      @addShiftRequested="handleAddShift"
      @autoScheduleRequested="handleAutoSchedule"
    />

    <CalendarDisplay
      ref="calendarDisplay"
      :events="calendarStore.events"
      :holidayHours="holidayHoursMap"
      @dateRangeChange="handleDateRangeChange"
      @eventClick="handleEventClick"
      @dateSelect="handleDateSelect"
      @eventContextMenu="handleEventContextMenu"
      @dayContextMenu="handleDayContextMenu"
      @eventChange="handleEventChange"
    />

    <CalendarContextMenu
      v-model:show="showContextMenu"
      :options="contextMenuOptions"
      :items="contextMenuItems"
      @itemSelected="handleContextMenuAction"
    />

    <ShiftDialog
      v-model:visible="shiftDialogVisible"
      :shift="selectedShift"
      :staffMembers="filteredStaffForStore"
      :shiftsService="shiftsService"
      :existingShifts="calendarStore.events"
      :isSaving="isSavingShift"
      @save="saveShift"
      @delete="deleteShift"
      @cancel="closeShiftDialog"
      @validation-error="handleValidationError"
    />

    <EmailScheduleModal
      v-model:visible="emailDialogVisible"
      :store="selectedStore"
      :staffMembers="staffMembersWithEmail"
      :calendarApi="calendarDisplay?.getCalendarApi()"
      :isAdmin="isAdmin"
      @send="handleSendEmail"
      @cancel="handleCancelEmail"
    />

    <HolidayHoursDialog
      v-model:visible="holidayHoursDialogVisible"
      :storeId="selectedStore?.id || 0"
      :date="holidayHoursDate"
      :holidayData="holidayHoursData"
      @saved="handleHolidayHoursSaved"
    />

    <AutoScheduleModal
      v-model:visible="autoScheduleModalVisible"
      :storeId="selectedStore?.id || 0"
      :storeName="selectedStore?.name || ''"
      :weekStartDay="selectedStore?.week_start_day || 1"
      @scheduleApplied="handleScheduleApplied"
    />
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";

// Import our components
import CalendarContextMenu from "../components/calendar/CalendarContextMenu.vue";
import CalendarDisplay from "../components/calendar/CalendarDisplay.vue";
import CalendarHeader from "../components/calendar/CalendarHeader.vue";
import ShiftDialog from "../components/calendar/ShiftDialog.vue";
import EmailScheduleModal from "../components/calendar/EmailScheduleModal.vue";
import HolidayHoursDialog from "../components/calendar/HolidayHoursDialog.vue";
import AutoScheduleModal from "../components/calendar/AutoScheduleModal.vue";

// Import our composables
import { useCalendarPrinting } from "../composables/useCalendarPrinting";
import { useCalendarEmail } from "../composables/useCalendarEmail";
import { useContextMenu } from "../composables/useContextMenu";
import { useShiftCopy } from "../composables/useShiftCopy";
import { useShiftDelete } from "../composables/useShiftDelete";
import { useShifts } from "../composables/useShifts";
import { useStaff } from "../composables/useStaff";
import { getHoursForDateRange } from "@/lib/api";

// Import time utilities
import { toJSDate } from "@/lib/timeUtils";

// Import our stores
import { useAuthStore } from "../stores/authStore";
import { useCalendarStore } from "../stores/calendarStore";

export default {
  name: "Calendar",

  components: {
    CalendarHeader,
    CalendarDisplay,
    CalendarContextMenu,
    ShiftDialog,
    EmailScheduleModal,
    HolidayHoursDialog,
    AutoScheduleModal,
  },

  setup() {
    const toast = useToast();
    const router = useRouter();
    const calendarDisplay = ref(null);

    // Initialize our composables and stores
    const calendarStore = useCalendarStore();
    const authStore = useAuthStore();
    const isAdmin = computed(() => authStore.isAdmin);
    const { fetchStaffMembers, getStaffByStore, createStaffMap } = useStaff();
    const shiftsService = useShifts();
    const { printCalendar } = useCalendarPrinting();
    const { emailCalendar } = useCalendarEmail();

    // Extract events as a ref using storeToRefs (preserves reactivity for composables)
    const { events } = storeToRefs(calendarStore);

    // Local state
    const stores = computed(() => calendarStore.stores);

    // Two-way computed property to sync with store
    const selectedStore = computed({
      get: () => calendarStore.selectedStore,
      set: (value) => (calendarStore.selectedStore = value),
    });

    // Watch for store changes and refresh the calendar
    watch(
      selectedStore,
      (newStore) => {
        if (newStore) {
          refreshCalendar();
        }
      },
      { immediate: false }
    );

    const staffMembers = ref([]);

    // Context menu state
    const showContextMenu = ref(false);
    const contextMenuOptions = ref({
      x: 0,
      y: 0,
      zIndex: 1000,
      minWidth: 200,
      customClass: "calendar-context-menu",
      theme: "default",
    });
    const contextMenuItems = ref([]);

    // Shift dialog state
    const selectedShift = ref(null);
    const shiftDialogVisible = ref(false);
    const isSavingShift = ref(false);

    // Email dialog state
    const emailDialogVisible = ref(false);

    // Holiday hours dialog state
    const holidayHoursDialogVisible = ref(false);
    const holidayHoursDate = ref(null);
    const holidayHoursData = ref(null);

    // Auto-schedule modal state
    const autoScheduleModalVisible = ref(false);

    // Holiday hours map for badges
    const holidayHoursMap = ref({});

    // Handle opening holiday hours dialog (defined early for useContextMenu)
    const openHolidayHoursDialog = (date) => {
      if (!selectedStore.value) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please select a store first',
          life: 3000
        });
        return;
      }

      holidayHoursDate.value = date;
      holidayHoursData.value = null;
      holidayHoursDialogVisible.value = true;
    };

    // Initialize the new modular composables (pass events ref, not unwrapped value)
    const shiftCopy = useShiftCopy(events, shiftsService);

    const shiftDelete = useShiftDelete(events, shiftsService);

    const contextMenu = useContextMenu(
      events,
      shiftCopy,
      shiftDelete,
      router,
      selectedStore,
      openHolidayHoursDialog
    );

    // Computed property to filter staff for the selected store
    const filteredStaffForStore = computed(() => {
      if (!selectedStore.value || !selectedShift.value)
        return staffMembers.value;

      const storeId = selectedShift.value.storeId;
      return getStaffByStore(storeId);
    });

    // Computed property to filter staff members with emails for the current store
    const staffMembersWithEmail = computed(() => {
      if (!selectedStore.value) return [];

      const storeStaff = getStaffByStore(selectedStore.value.id);
      return storeStaff.filter(staff => staff.email && staff.email.trim() !== '');
    });

    // Handle date range change in calendar
    const handleDateRangeChange = async (dateInfo) => {
      const staffMap = createStaffMap();
      await calendarStore.fetchShifts(
        dateInfo.startStr,
        dateInfo.endStr,
        staffMap
      );

      // Fetch holiday hours for the date range
      if (selectedStore.value) {
        try {
          const response = await getHoursForDateRange(
            selectedStore.value.id,
            dateInfo.startStr.split('T')[0],
            dateInfo.endStr.split('T')[0]
          );
          // Build lookup map: date string -> holiday data
          const hoursArray = response.data.hours || [];
          const hoursMap = {};
          hoursArray.forEach(dayHours => {
            // Only include days with holiday overrides
            if (dayHours.source === 'holiday') {
              hoursMap[dayHours.date] = {
                is_closed: dayHours.is_closed,
                is_holiday: true,
                open: dayHours.open,
                close: dayHours.close,
                note: dayHours.note
              };
            }
          });
          holidayHoursMap.value = hoursMap;
        } catch (error) {
          console.error('Failed to fetch holiday hours:', error);
          holidayHoursMap.value = {};
        }
      } else {
        holidayHoursMap.value = {};
      }
    };

    // Disable FullCalendar's keyboard capture when opening dialog
    const disableCalendarFocus = () => {
      const fcContainer = document.querySelector('.fc');
      if (fcContainer) {
        fcContainer.setAttribute('data-original-tabindex', fcContainer.getAttribute('tabindex') || '0');
        fcContainer.setAttribute('tabindex', '-1');
        // Also blur any focused element inside the calendar
        const focused = fcContainer.querySelector(':focus');
        focused?.blur();
      }
      document.activeElement?.blur();
    };

    // Restore FullCalendar's keyboard accessibility
    const restoreCalendarFocus = () => {
      const fcContainer = document.querySelector('.fc');
      if (fcContainer) {
        const original = fcContainer.getAttribute('data-original-tabindex');
        if (original !== null) {
          fcContainer.setAttribute('tabindex', original);
          fcContainer.removeAttribute('data-original-tabindex');
        }
      }
    };

    // Handle clicking on an event
    const handleEventClick = (event) => {
      // Disable FullCalendar's keyboard capture before opening dialog
      disableCalendarFocus();

      const userId = event.extendedProps.userId;
      const storeId = event.extendedProps.storeId;

      if (isAdmin.value) {
        // Admin can edit the shift
        selectedShift.value = {
          id: event.id,
          title: event.title,
          startTime: toJSDate(event.start),
          endTime: toJSDate(event.end),
          date: toJSDate(event.start),
          userId: userId,
          storeId: storeId,
          notes: event.extendedProps.notes,
        };
      } else {
        // Non-admin just sees the details
        selectedShift.value = {
          id: event.id,
          title: event.title,
          startTime: toJSDate(event.start),
          endTime: toJSDate(event.end),
          date: toJSDate(event.start),
          userId: userId,
          storeId: storeId,
          notes: event.extendedProps.notes,
          readOnly: true, // Add this flag to indicate read-only mode
        };
      }

      shiftDialogVisible.value = true;
    };

    // Handle selecting a date range
    const handleDateSelect = (selectInfo) => {
      if (!isAdmin.value) return;

      // Disable FullCalendar's keyboard capture before opening dialog
      disableCalendarFocus();

      const storeId = selectedStore.value ? selectedStore.value.id : null;

      selectedShift.value = {
        id: null,
        title: "",
        startTime: toJSDate(selectInfo.start),
        endTime: toJSDate(selectInfo.end),
        date: toJSDate(selectInfo.start),
        userId: null,
        storeId: storeId,
        notes: "",
      };

      shiftDialogVisible.value = true;
    };

    // Handle drag & drop event changes
    const handleEventChange = async (event) => {
      if (!isAdmin.value) {
        // Refresh calendar to revert the visual change for non-admins
        refreshCalendar();
        return;
      }

      const userId = event.extendedProps.userId;
      const storeId = event.extendedProps.storeId;

      // Create shift object with updated times
      const updatedShift = {
        id: event.id,
        title: event.title,
        startTime: toJSDate(event.start),
        endTime: toJSDate(event.end),
        date: toJSDate(event.start),
        userId: userId,
        storeId: storeId,
        notes: event.extendedProps.notes,
      };

      try {
        // Use shiftsService to save the updated shift
        const result = await shiftsService.saveShift(updatedShift, { existingShifts: calendarStore.events });

        if (result && result.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Shift updated successfully",
            life: 3000,
          });
        } else if (result && !result.success) {
          // Handle specific errors
          let errorMessage = "Failed to update shift";
          if (result.error === "conflict") {
            errorMessage = "Scheduling conflict detected";
          } else if (result.error === "duplicate") {
            errorMessage = "This would create a duplicate shift";
          } else if (result.message) {
            errorMessage = result.message;
          }

          toast.add({
            severity: "error",
            summary: "Error",
            detail: errorMessage,
            life: 3000,
          });

          // Refresh calendar to revert the visual change
          refreshCalendar();
        }
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to update shift",
          life: 3000,
        });

        // Refresh calendar to revert the visual change
        refreshCalendar();
      }
    };

    // Handle right-click on an event
    const handleEventContextMenu = (e, event) => {
      if (!isAdmin.value) return; // Early return for non-admins

      const menuItems = contextMenu.buildContextMenuOptions("shift", event);
      if (menuItems.length === 0) return;

      contextMenuItems.value = menuItems;
      contextMenuOptions.value = {
        ...contextMenuOptions.value,
        x: e.x,
        y: e.y,
      };

      showContextMenu.value = true;
    };

    // Handle right-click on a day
    const handleDayContextMenu = (e, data) => {
      if (!isAdmin.value) return; // Early return for non-admins

      const menuItems = contextMenu.buildContextMenuOptions("day", data);
      if (menuItems.length === 0) return;

      contextMenuItems.value = menuItems;
      contextMenuOptions.value = {
        ...contextMenuOptions.value,
        x: e.x,
        y: e.y,
      };

      showContextMenu.value = true;
    };

    // Handle context menu item selection
    const handleContextMenuAction = async (item) => {
      try {
        const result = await item.onClick();

        // Show toast notification if operation had a result
        if (result && result.message) {
          toast.add({
            severity: result.success ? "success" : "error",
            summary: result.success ? "Success" : "Error",
            detail: result.message,
            life: 3000,
          });
        }

        // Refresh calendar after operation completes
        refreshCalendar();
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error.message || "An error occurred",
          life: 3000,
        });
      }
    };

    // Close shift dialog
    const closeShiftDialog = () => {
      selectedShift.value = null;
      shiftDialogVisible.value = false;
      // Restore FullCalendar's keyboard accessibility
      restoreCalendarFocus();
    };

    // Handle validation errors from ShiftDialog
    const handleValidationError = (message) => {
      toast.add({
        severity: "warn",
        summary: "Validation Error",
        detail: message,
        life: 3000,
      });
    };

    // Save a shift with conflict/duplicate handling
    const saveShift = async (shift, options = {}) => {
      isSavingShift.value = true;
      try {
        const result = await shiftsService.saveShift(shift, { ...options, existingShifts: calendarStore.events });

        // Handle conflicts/duplicates
        if (result && !result.success) {
          if (result.error === "conflict") {
            toast.add({
              severity: "warn",
              summary: "Scheduling Conflict",
              detail: result.message,
              life: 5000,
            });
            return;
          } else if (result.error === "duplicate") {
            toast.add({
              severity: "warn",
              summary: "Duplicate Shift",
              detail: result.message,
              life: 5000,
            });
            return;
          } else {
            toast.add({
              severity: "error",
              summary: "Error",
              detail: result.message || "Failed to save shift",
              life: 3000,
            });
            return;
          }
        }

        // Success case
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Shift saved successfully",
          life: 3000,
        });
        refreshCalendar();
        closeShiftDialog();
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to save shift",
          life: 3000,
        });
      } finally {
        isSavingShift.value = false;
      }
    };

    // Delete a shift
    const deleteShift = async () => {
      if (!selectedShift.value || !selectedShift.value.id) return;

      try {
        await shiftsService.deleteShift(selectedShift.value.id);
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Shift deleted successfully",
          life: 3000,
        });
        refreshCalendar();
        closeShiftDialog();
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete shift",
          life: 3000,
        });
      }
    };

    // Refresh calendar data
    const refreshCalendar = async () => {
      const calendarApi = calendarDisplay.value?.getCalendarApi();
      if (calendarApi) {
        const staffMap = createStaffMap();
        try {
          // Fetch shifts with the current store setting
          await calendarStore.fetchShifts(
            calendarApi.view.activeStart.toISOString(),
            calendarApi.view.activeEnd.toISOString(),
            staffMap
          );
        } catch (error) {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to refresh calendar data",
            life: 3000,
          });
        }
      }
    };

    // Handle print calendar request
    const handlePrintCalendar = () => {
      if (!selectedStore.value || !selectedStore.value.id) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please select a store before printing the schedule",
          life: 3000,
        });
        return;
      }

      printCalendar({
        calendarDisplay: calendarDisplay.value,
        selectedStore: selectedStore.value,
        showToast: toast.add,
      });
    };

    // Handle email calendar request
    const handleEmailCalendar = () => {
      if (!selectedStore.value || !selectedStore.value.id) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please select a store before emailing the schedule",
          life: 3000,
        });
        return;
      }

      emailDialogVisible.value = true;
    };

    // Handle sending email
    const handleSendEmail = async (emailData) => {
      try {
        const calendarApi = calendarDisplay.value?.getCalendarApi();
        if (!calendarApi) {
          throw new Error("Calendar API not available");
        }

        // Determine if we're in week or month view
        const viewType = calendarApi.view.type;

        const result = await emailCalendar({
          calendarApi,
          selectedStore: selectedStore.value,
          emailData,
          viewType,
        });

        if (result && result.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Schedule email sent successfully",
            life: 3000,
          });
          emailDialogVisible.value = false;
        } else {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: result?.message || "Failed to send email",
            life: 3000,
          });
        }
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error.message || "Failed to send email",
          life: 3000,
        });
      }
    };

    // Handle email cancel
    const handleCancelEmail = () => {
      emailDialogVisible.value = false;
    };
    // Handle holiday hours saved
    const handleHolidayHoursSaved = async () => {
      // Refresh calendar to show updated holiday hours
      await refreshCalendar();

      // Also refresh the holiday hours map
      const calendarApi = calendarDisplay.value?.getCalendarApi();
      if (calendarApi && selectedStore.value) {
        try {
          const response = await getHoursForDateRange(
            selectedStore.value.id,
            calendarApi.view.activeStart.toISOString().split('T')[0],
            calendarApi.view.activeEnd.toISOString().split('T')[0]
          );
          // Build lookup map: date string -> holiday data
          const hoursArray = response.data.hours || [];
          const hoursMap = {};
          hoursArray.forEach(dayHours => {
            // Only include days with holiday overrides or special status
            if (dayHours.source === 'holiday' || dayHours.is_holiday) {
              hoursMap[dayHours.date] = {
                is_closed: dayHours.is_closed,
                is_holiday: dayHours.source === 'holiday',
                open: dayHours.open,
                close: dayHours.close,
                note: dayHours.note
              };
            }
          });
          holidayHoursMap.value = hoursMap;
        } catch (error) {
          console.error('Failed to refresh holiday hours:', error);
        }
      }
    };

    // Handle adding a new shift
    const handleAddShift = () => {
      if (!selectedStore.value) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please select a store before adding a shift",
          life: 3000,
        });
        return;
      }

      const now = new Date();
      const startTime = new Date(now);
      startTime.setHours(9, 0, 0, 0); // Default to 9:00 AM

      const endTime = new Date(now);
      endTime.setHours(17, 0, 0, 0); // Default to 5:00 PM

      selectedShift.value = {
        id: null,
        title: "",
        startTime: startTime,
        endTime: endTime,
        date: now,
        userId: null,
        storeId: selectedStore.value.id,
        notes: "",
      };

      shiftDialogVisible.value = true;
    };

    // Handle auto-schedule request
    const handleAutoSchedule = () => {
      if (!selectedStore.value) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please select a store before auto-scheduling",
          life: 3000,
        });
        return;
      }

      autoScheduleModalVisible.value = true;
    };

    // Handle schedule applied from auto-schedule modal
    const handleScheduleApplied = async () => {
      autoScheduleModalVisible.value = false;
      await refreshCalendar();
      // Toast already shown by useAutoSchedule composable
    };

    // Initialize data
    onMounted(async () => {
      try {
        // Fetch staff members first
        staffMembers.value = await fetchStaffMembers();

        // Fetch stores
        await calendarStore.fetchStores();

        // Initial calendar load
        const calendarApi = calendarDisplay.value?.getCalendarApi();
        if (calendarApi) {
          const staffMap = createStaffMap();
          await calendarStore.fetchShifts(
            calendarApi.view.activeStart.toISOString(),
            calendarApi.view.activeEnd.toISOString(),
            staffMap
          );
        }
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load initial data",
          life: 3000,
        });
      }
    });

    return {
      // Refs
      calendarDisplay,

      // Store (for template access)
      calendarStore,

      // State
      stores,
      selectedStore,
      staffMembers,
      showContextMenu,
      contextMenuOptions,
      contextMenuItems,
      selectedShift,
      shiftDialogVisible,
      isSavingShift,
      emailDialogVisible,
      holidayHoursDialogVisible,
      holidayHoursDate,
      holidayHoursData,
      holidayHoursMap,
      filteredStaffForStore,
      staffMembersWithEmail,
      isAdmin,
      autoScheduleModalVisible,

      // Methods
      handleDateRangeChange,
      handleValidationError,
      handleEventClick,
      handleDateSelect,
      handleEventContextMenu,
      handleDayContextMenu,
      handleEventChange,
      handleContextMenuAction,
      closeShiftDialog,
      saveShift,
      deleteShift,
      handlePrintCalendar,
      handleEmailCalendar,
      handleSendEmail,
      handleCancelEmail,
      openHolidayHoursDialog,
      handleHolidayHoursSaved,
      refreshCalendar,
      handleAddShift,
      handleAutoSchedule,
      handleScheduleApplied,

      // Services and helpers
      shiftsService,
    };
  },
};
</script>

<style scoped>
.calendar-container {
  padding: 1rem;
}

/* ============================================
   SCREEN STYLES - Keep events readable
   ============================================ */

/* Prevent event text wrapping */
:deep(.fc-event-title) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ============================================
   PRINT STYLES - Portrait 8.5" x 11"
   Fits 6 weeks + 5 shifts per cell
   ============================================ */
@media print {
  /* Page setup */
  @page {
    size: letter portrait;
    margin: 0.4in;
  }

  /* Reset container */
  .calendar-container {
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }

  /* Hide non-print elements */
  .calendar-header {
    display: none !important;
  }

  /* Hide FullCalendar toolbar */
  :deep(.fc-header-toolbar) {
    display: none !important;
  }

  /* Calendar container */
  :deep(.fc) {
    font-size: 8px !important;
    height: auto !important;
    max-height: none !important;
  }

  /* Force table layout for consistent sizing */
  :deep(.fc-scrollgrid) {
    border: 1px solid #333 !important;
  }

  :deep(.fc-scrollgrid-section),
  :deep(.fc-scrollgrid-section > td) {
    height: auto !important;
  }

  /* Day header row (Sun Mon Tue...) */
  :deep(.fc-col-header-cell) {
    padding: 4px 2px !important;
    font-size: 9px !important;
    font-weight: bold !important;
    background: #f0f0f0 !important;
    border: 1px solid #999 !important;
  }

  :deep(.fc-col-header-cell-cushion) {
    padding: 2px !important;
  }

  /* Day cells - calculated for 6-week fit */
  :deep(.fc-daygrid-day) {
    border: 1px solid #999 !important;
    height: auto !important;
    min-height: 0 !important;
  }

  :deep(.fc-daygrid-day-frame) {
    min-height: 115px !important; /* ~890px grid / 6 rows - padding */
    max-height: 130px !important;
  }

  /* Day number - right aligned, compact */
  :deep(.fc-daygrid-day-top) {
    flex-direction: row-reverse;
    justify-content: flex-start;
  }

  :deep(.fc-daygrid-day-number) {
    font-size: 10px !important;
    font-weight: bold !important;
    padding: 2px 4px 0 2px !important;
    color: #333 !important;
  }

  /* "BREAK GLASS" option - uncomment to reclaim day-number left space */
  /* :deep(.fc-daygrid-day-top) {
    flex-direction: row;
    justify-content: flex-start;
  }
  :deep(.fc-daygrid-day-number) {
    padding: 2px 2px 0 2px !important;
  } */

  /* Events container */
  :deep(.fc-daygrid-day-events) {
    margin: 0 !important;
    padding: 1px !important;
  }

  /* Individual events - super compact */
  :deep(.fc-daygrid-event) {
    margin: 1px 0 !important;
    border-radius: 2px !important;
  }

  :deep(.fc-event) {
    padding: 1px 2px !important;
    font-size: 8px !important;
    line-height: 1.25 !important;
    border: none !important;
    background-color: #e8e8e8 !important;
    color: #000 !important;
  }

  :deep(.fc-event-main) {
    padding: 0 !important;
  }

  :deep(.fc-event-title) {
    font-weight: normal !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  /* More link (+2 more) */
  :deep(.fc-daygrid-more-link) {
    font-size: 7px !important;
    margin: 1px 0 !important;
  }

  /* Hide scrollbars */
  :deep(.fc-scroller) {
    overflow: visible !important;
  }

  :deep(.fc-scroller-liquid-absolute) {
    position: relative !important;
  }

  /* Outside month days - lighter */
  :deep(.fc-day-other) {
    background: #fafafa !important;
  }

  :deep(.fc-day-other .fc-daygrid-day-number) {
    color: #999 !important;
  }

  /* Today highlighting - subtle for print */
  :deep(.fc-day-today) {
    background: #fff !important;
  }

  /* Ensure black text for printing */
  :deep(.fc-daygrid-day-number),
  :deep(.fc-col-header-cell-cushion),
  :deep(.fc-event-title) {
    color: #000 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Page break prevention */
  :deep(.fc-view),
  :deep(.fc-daygrid),
  :deep(.fc-scrollgrid) {
    page-break-inside: avoid !important;
  }
}
</style>
